import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import * as Const from "../constants/ThresholdsConstants";
import {addFilter, getFilterValue, renderMultiSelect} from "../../../utils/FilterUtils";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { STATE_ACTIVE_FILTERS } from "../constants/ThresholdsConfigurationRecoil";
import AdvancedFilter from "../../../component/AdvancedFilter";
import * as Renders from "../../../component/AdvancedFiltersRenders";
import { InputAdornment } from "@mui/material";
import { Euro, Percent } from "@mui/icons-material";
import {useContext, useState} from "react";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {ID_FILTER_THRESHOLD_LEGAL_ENTITIES, ID_FILTER_THRESHOLD_DEALERS} from "../constants/ThresholdsConstants";

function ThresholdConfigurationFilter() {
    const { t } = useTranslation("finance", { keyPrefix: "cashback" });
    const { t: tg } = useTranslation("translation");

    const setFilters = useSetRecoilState(STATE_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const { globalData: { user } } = useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const [dealers, setDealers] = useState<any[]>(dealerOptions);

    const schema = Yup.object().shape({
        [Const.ID_FILTER_CUSTOMERNAME_TS]: Yup.string().max(
            128,
            t("filters.errors.invalid-length")
        ),
        [Const.ID_FILTER_TYPE_TS]: Yup.string(),
        [Const.ID_FILTER_PERCENTAGE_FROM_TS]: Yup.number().transform((value, originalValue) => {
            return originalValue.trim() === '' ? undefined : parseFloat(originalValue);
        }).max(100, t("filters.errors.invalid-percentage")).nullable(),
        [Const.ID_FILTER_PERCENTAGE_TO_TS]: Yup.number().transform((value, originalValue) => {
            return originalValue.trim() === '' ? undefined : parseFloat(originalValue);
        }).max(100, t("filters.errors.invalid-percentage")).test('percentage', t('filters.errors.invalid-percentage'), function (value) {
            const fromValue = this.parent[Const.ID_FILTER_PERCENTAGE_FROM_TS];
            return fromValue === undefined || value === undefined || value >= fromValue;
        }).nullable(),
        [Const.ID_FILTER_THRESHOLD_FROM_TS]: Yup.number().transform((value, originalValue) => {
            return originalValue.trim() === '' ? undefined : parseFloat(originalValue);
        }).test('threshold', t('filters.errors.invalid-threshold'), function (value) {
            const toValue = this.parent[Const.ID_FILTER_THRESHOLD_TO_TS];
            return toValue === undefined || value === undefined || value <= toValue;
        }).nullable(),
        [Const.ID_FILTER_THRESHOLD_TO_TS]: Yup.number().transform((value, originalValue) => {
            return originalValue.trim() === '' ? undefined : parseFloat(originalValue);
        }).test('threshold', t('filters.errors.invalid-threshold'), function (value) {
            const fromValue = this.parent[Const.ID_FILTER_THRESHOLD_FROM_TS];
            return fromValue === undefined || value === undefined || value >= fromValue;
        }).nullable(),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne'))

    });

    const customDefaultValues = {
        [Const.ID_FILTER_CUSTOMERNAME_TS]: "",
        [Const.ID_FILTER_TYPE_TS]:  "",
        [Const.ID_FILTER_THRESHOLD_FROM_TS]: "",
        [Const.ID_FILTER_THRESHOLD_TO_TS]: "",
        [Const.ID_FILTER_PERCENTAGE_FROM_TS]: "",
        [Const.ID_FILTER_PERCENTAGE_TO_TS]: "",
        legalEntities:
            getFilterValue(ID_FILTER_THRESHOLD_LEGAL_ENTITIES, filterDefs) ||
            legalEntityOptions.map((x: any) => x.value),
        dealers: []
    };

    const defaultValues = {
        [Const.ID_FILTER_CUSTOMERNAME_TS]: getFilterValue(Const.ID_FILTER_CUSTOMERNAME_TS, filterDefs) || "",
        [Const.ID_FILTER_TYPE_TS]: getFilterValue(Const.ID_FILTER_TYPE_TS, filterDefs) || "",
        [Const.ID_FILTER_THRESHOLD_FROM_TS]: getFilterValue(Const.ID_FILTER_THRESHOLD_FROM_TS, filterDefs) || "",
        [Const.ID_FILTER_THRESHOLD_TO_TS]: getFilterValue(Const.ID_FILTER_THRESHOLD_TO_TS, filterDefs) || "",
        [Const.ID_FILTER_PERCENTAGE_FROM_TS]: getFilterValue(Const.ID_FILTER_PERCENTAGE_FROM_TS, filterDefs) || "",
        [Const.ID_FILTER_PERCENTAGE_TO_TS]: getFilterValue(Const.ID_FILTER_PERCENTAGE_TO_TS, filterDefs) || "",
        legalEntities:
            getFilterValue(ID_FILTER_THRESHOLD_LEGAL_ENTITIES, filterDefs) ||
            legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_THRESHOLD_DEALERS, filterDefs) || [],
    };

    const percentInputProps = {
        inputMode: 'decimal',
        pattern: '^\\d+(\\.\\d{1,2})?$'
    };

    const fields = {
        main: {
            [Const.ID_FILTER_CUSTOMERNAME_TS]: Renders.renderTextField(
                Const.ID_FILTER_CUSTOMERNAME_TS,
                t("thresholds.filters." + Const.ID_FILTER_CUSTOMERNAME_TS),
            ),
            [Const.ID_FILTER_TYPE_TS]: Renders.renderSelect(
                Const.ID_FILTER_TYPE_TS,
                t("thresholds.filters." + Const.ID_FILTER_TYPE_TS),
                Const.transactionType.map((x) => {
                    return { ...x, label: t(x.label) };
                })
            )
        },
        adv: {
            [Const.ID_FILTER_THRESHOLD_FROM_TS]: Renders.renderTextField(
                Const.ID_FILTER_THRESHOLD_FROM_TS,
                t("thresholds.filters." + Const.ID_FILTER_THRESHOLD_FROM_TS),
                "number",
                {
                    startAdornment:
                        <InputAdornment position="start">
                            <Euro style={{ fontSize: '15px' }} />
                        </InputAdornment>

                }
            ),
            [Const.ID_FILTER_THRESHOLD_TO_TS]: Renders.renderTextField(
                Const.ID_FILTER_THRESHOLD_TO_TS,
                t("thresholds.filters." + Const.ID_FILTER_THRESHOLD_TO_TS),
                "number",
                {
                    startAdornment:
                        <InputAdornment position="start">
                            <Euro style={{ fontSize: '15px' }} />
                        </InputAdornment>

                }
            ),
            [Const.ID_FILTER_PERCENTAGE_FROM_TS]: Renders.renderTextField(
                Const.ID_FILTER_PERCENTAGE_FROM_TS,
                t("thresholds.filters." + Const.ID_FILTER_PERCENTAGE_FROM_TS),
                "number",
                {
                    startAdornment:
                        <InputAdornment position="start">
                            <Percent style={{ fontSize: '15px' }} />
                        </InputAdornment>
                    ,
                    percentInputProps

                }
            ),
            [Const.ID_FILTER_PERCENTAGE_TO_TS]: Renders.renderTextField(
                Const.ID_FILTER_PERCENTAGE_TO_TS,
                t("thresholds.filters." + Const.ID_FILTER_PERCENTAGE_TO_TS),
                "number",
                {
                    startAdornment:
                        <InputAdornment position="start">
                            <Percent style={{ fontSize: '15px' }} />
                        </InputAdornment>
                    ,
                    percentInputProps
                }
            ),
            legalEntities: renderMultiSelect(
                tg("shared.legalEntities"),
                legalEntityOptions,
                "legalEntities",
                legalEntityOptions.length <= 1,
                (value: any, setValue: any) => {
                    setValue('dealers', []);
                    let dealers = user?.dealers
                        ?.filter(
                            (x: any) =>
                                value?.length > 0 &&
                                x.legalEntities.some((e: any) => value.includes(e.name)) &&
                                x.name.toLowerCase() !== "all"
                        )
                        .map((x: any) => {
                            return {
                                label: tg(`dealers.${x.name}`),
                                value: x.name,
                            };
                        });
                    setDealers(dealers);
                }
            ),
            dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1)

        }
    };

    const submit = (data: any) => {

        Object.keys(defaultValues)
            .forEach((param) => {
                addFilter(setFilters, param, data[param]);
            });

        addFilter(setFilters, ID_FILTER_THRESHOLD_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_THRESHOLD_DEALERS, data.dealers);
    };

    const reset = () => {
        setFilters([]);
    };

    return (
        <AdvancedFilter
            schema={schema}
            mainFields={fields.main}
            fields={fields.adv}
            defaultValues={defaultValues}
            onSubmit={submit}
            onReset={reset}
            customDefaultValues={customDefaultValues}
        />
    );
}

export default ThresholdConfigurationFilter;
